import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { SelectCard, Skeleton } from '@/design-system'
import { AlbumPhoto } from '@/features/album/types'

export interface StudioAlbumPrintPickerProps {
  albumTitle?: string
  photos: AlbumPhoto[]
  loading: boolean
  /** Currently-applied print id, e.g. `egoID-123` — matched against each photo's `egoID-{egoId}`. */
  selectedValue?: string | number
  /** Fires with the full print-selection value, e.g. `egoID-123` (ready to feed straight into `onSelect('print', ...)`). */
  onSelect: (printValue: string) => void
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 12,
})

const EmptyBox = styled(Box)({
  height: 140,
  borderRadius: 'var(--ds-radius-md)',
  border: '1.5px dashed var(--ds-border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 'var(--ds-space-4)',
})

/** Grid of the gallery's source album photos — picking one primes the renderer to place it on the next wall click. */
export function StudioAlbumPrintPicker({
  albumTitle,
  photos,
  loading,
  selectedValue,
  onSelect,
}: StudioAlbumPrintPickerProps) {
  if (loading) {
    return (
      <Grid>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} rounded='md' width='100%' height={110} />
        ))}
      </Grid>
    )
  }

  if (photos.length === 0) {
    return (
      <EmptyBox>
        <Typography sx={{ fontSize: 12.5, color: 'var(--ds-ink-subtle)', lineHeight: 1.5 }}>
          No photos in {albumTitle ? `“${albumTitle}”` : 'this album'} yet. Add some from the album
          page, then come back to place them on the walls.
        </Typography>
      </EmptyBox>
    )
  }

  return (
    <Grid>
      {photos.map((photo) => {
        const value = `egoID-${photo.egoId}`
        return (
          <SelectCard
            key={photo.id}
            // Prefer the full-res URL over the pre-baked "album" square crop —
            // some sources bake letterbox/blur padding into that variant when
            // the original isn't square, which shows through here since this
            // tile forces a 1:1 box. object-fit: cover on the untouched full
            // image crops cleanly instead of reproducing that padding.
            imageUrl={photo.url || photo.thumbUrl}
            aspectRatio='1 / 1'
            selected={selectedValue === value}
            onClick={() => photo.egoId && onSelect(value)}
          />
        )
      })}
    </Grid>
  )
}
